// src/components/AugustTabIcons.js
// Hand-drawn single-stroke ink icons for the august tab bar — same aesthetic as the landing
// page's feature icons (public/landing-new.html: ritual/journal, moments, journey). Today,
// Journey, and Moments reuse those SVGs verbatim (concepts overlap 1:1); Tracklist, Favorites,
// and Profile are new, drawn to match (viewBox 0 0 40 40, round caps/joins, currentColor).

function IconBase({ size, color, children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ color }}>
      {children}
    </svg>
  )
}

// Reused from landing-new.html's "One small ritual a day" feature icon.
export function TodayIcon({ size = 20, color = 'currentColor' }) {
  return (
    <IconBase size={size} color={color}>
      <path d="M4,10 C4,8 6,7 8,7.5 L19,9 L19,32 L8,30.5 C6,30 4,29 4,27 Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M36,10 C36,8 34,7 32,7.5 L21,9 L21,32 L32,30.5 C34,30 36,29 36,27 Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M19,9 L21,9 M19,32 L21,32" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8,14.2 L15,14.7 M8,18.1 L15,18.5 M8,22 L13,22.4" stroke="currentColor" strokeWidth="1.2" />
    </IconBase>
  )
}

// Reused from landing-new.html's "Twenty-eight small days, one habit" feature icon.
export function JourneyIcon({ size = 20, color = 'currentColor' }) {
  return (
    <IconBase size={size} color={color}>
      <path d="M6,32 C10,28 8,22 13,20 C18,18 14,12 19,9 C23,6.5 27,9 30,7" stroke="currentColor" strokeWidth="1.8" />
      <path d="M27,5.5 L30.5,7 L28,10" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5,32 C5.3,31.3 6.6,31.2 6.9,32 C7.1,32.7 6,33.3 5.3,32.8 C5,32.6 4.9,32.3 5,32 Z" fill="currentColor" stroke="none" />
    </IconBase>
  )
}

// Reused from landing-new.html's "Keep the moment, not just the words" feature icon.
export function MomentsIcon({ size = 20, color = 'currentColor' }) {
  return (
    <IconBase size={size} color={color}>
      <path d="M6,16 C5.6,13 8.1,10.4 11,11.3 L14.6,10.7 L16,7.7 L24.2,8.4 L26.3,11.6 L29.9,10.8 C33.2,10.5 34.7,13.6 34.1,16.1 L34.6,27.6 C34.9,30.9 31.7,32.5 28.9,31.6 L10,32.7 C6.9,33.1 5.4,30.1 6,27.4 Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M6.4,16.5 C6.1,13.7 8.2,11.1 10.9,11.7" stroke="currentColor" strokeWidth="1.1" opacity="0.55" />
      <path d="M20.4,16 C25,15 28.2,18.9 27,23.4 C25.9,27.6 20.8,29.1 17.7,26.7 C14.6,24.4 14.2,19.7 16.8,17 C17.8,15.9 19.2,16.4 20.4,16 Z" stroke="currentColor" strokeWidth="1.7" />
      <path d="M10.4,14.2 L13,14.6" stroke="currentColor" strokeWidth="1.1" />
    </IconBase>
  )
}

// New — vinyl record, matching the app's own "Side A / Side B" tracklist framing.
export function TracklistIcon({ size = 20, color = 'currentColor' }) {
  return (
    <IconBase size={size} color={color}>
      <path d="M20,5.6 C27.8,6.3 34.5,12.7 33.7,20.2 C34.5,27.6 27.8,34.1 20,33.7 C12.2,34.1 5.5,27.6 6.3,20.2 C5.5,12.7 12.2,6.3 20,5.6 Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M20,15 C22.76,15.1 25.1,17.3 25,20 C25.1,22.7 22.76,24.9 20,25 C17.24,24.9 14.9,22.7 15,20 C14.9,17.3 17.24,15.1 20,15 Z" stroke="currentColor" strokeWidth="1.3" />
      <path d="M19.4,20 C19.4,19.55 19.8,19.2 20.3,19.3 C20.8,19.4 20.9,19.9 20.6,20.3 C20.3,20.7 19.7,20.6 19.4,20.2 Z" fill="currentColor" stroke="none" />
      <path d="M20,8.3 L20,11.2 M30.5,14.5 L28.3,16.2" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    </IconBase>
  )
}

// New — four-point sparkle, echoing the "✦" mark used throughout the Practices copy
// (Practice Complete, Today's Practice, etc.) so the tab icon and the feature's own
// typographic motif read as the same idea.
export function PracticesIcon({ size = 20, color = 'currentColor' }) {
  return (
    <IconBase size={size} color={color}>
      <path d="M20,6 C21,14 26,19 34,20 C26,21 21,26 20,34 C19,26 14,21 6,20 C14,19 19,14 20,6 Z" stroke="currentColor" strokeWidth="1.7" />
      <path d="M30.5,7.5 C30.8,9 31.7,9.9 33.4,10.2 C31.7,10.5 30.8,11.4 30.5,12.9 C30.2,11.4 29.3,10.5 27.6,10.2 C29.3,9.9 30.2,9 30.5,7.5 Z" stroke="currentColor" strokeWidth="1.1" opacity="0.55" />
    </IconBase>
  )
}

// New — simple hand-drawn bust silhouette.
export function ProfileIcon({ size = 20, color = 'currentColor' }) {
  return (
    <IconBase size={size} color={color}>
      <path d="M20,6.6 C23.7,6.4 27,9.5 26.6,13.2 C27,16.9 23.7,20 20,19.8 C16.3,20 13,16.9 13.4,13.2 C13,9.5 16.3,6.4 20,6.6 Z" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8,34 C8.5,27 13,22.5 20,22.7 C27,22.5 31.5,27 32,34" stroke="currentColor" strokeWidth="1.8" />
      <path d="M9,32.5 C9.6,28 12,24.5 16,23" stroke="currentColor" strokeWidth="1.1" opacity="0.5" />
    </IconBase>
  )
}
